/* eslint-disable react/prop-types */
import React, {useState, useRef, useCallback} from 'react';
import {
  Button,
  CircularProgress,
  TextField,
  LinearProgress,
} from '@mui/material';
import {ArrowForward, ArrowBack, CancelOutlined} from '@mui/icons-material';
import InputMask from 'react-input-mask';

// ─── API ──────────────────────────────────────────────────────────────────────
const API_BASE = 'https://kops.apiireit.com/gateway';

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
  danger: '#e53935',
  warn: '#d97706',
};

// ─── CNH categories ───────────────────────────────────────────────────────────
const CNH_CATS = [
  {id: 'A', icon: '🏍️', label: 'A', desc: 'Motos'},
  {id: 'B', icon: '🚗', label: 'B', desc: 'Carros'},
  {id: 'AB', icon: '🏍️🚗', label: 'AB', desc: 'Moto e Carro'},
  {id: 'D', icon: '🚌', label: 'D', desc: 'Van / Ônibus'},
  {id: 'AD', icon: '🏍️🚌', label: 'AD', desc: 'Moto e Van'},
  {id: 'E', icon: '🚛', label: 'E', desc: 'Combinações'},
];

// ─── Date helpers ─────────────────────────────────────────────────────────────
function parseDate(str) {
  if (!str || str.replace(/\D/g, '').length < 8) return null;
  const [d, m, y] = str.split('/').map(Number);
  if (!d || !m || !y) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d)
    return null;
  return dt;
}

function isFutureDate(str) {
  const dt = parseDate(str);
  if (!dt) return null;
  return dt > new Date();
}

// Days until expiry
function daysUntil(str) {
  const dt = parseDate(str);
  if (!dt) return null;
  return Math.round((dt - new Date()) / (1000 * 60 * 60 * 24));
}

// ─── CNH photo requirements ───────────────────────────────────────────────────
const CNH_REQS = [
  {ok: true, text: 'Documento aberto, frente visível'},
  {ok: true, text: 'Todos os campos legíveis (nome, categoria, validade)'},
  {ok: true, text: 'Foto nítida, sem borrado nem reflexo'},
  {ok: true, text: 'CNH digital (print da tela) é válida normalmente'},
  {ok: false, text: 'Sem foto cortada ou parcialmente visível'},
  {ok: false, text: 'Sem reflexo de luz ou sombra sobre o documento'},
];

// ─── SVG: CNH document illustration ──────────────────────────────────────────
const CnhIllustration = ({good}) => (
  <svg
    viewBox="0 0 200 130"
    width="100%"
    height="100%"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Card background */}
    <rect
      x={good ? 20 : 14}
      y={good ? 15 : 10}
      width="160"
      height="100"
      rx="8"
      fill={good ? '#eef6ff' : '#fff5f5'}
      stroke={good ? C.azul : '#fca5a5'}
      strokeWidth="2"
      transform={good ? '' : 'rotate(-5 100 65)'}
    />

    {/* Header stripe */}
    <rect
      x={good ? 20 : 14}
      y={good ? 15 : 10}
      width="160"
      height="22"
      rx="8"
      fill={good ? C.azul : '#ef4444'}
      transform={good ? '' : 'rotate(-5 100 65)'}
    />
    <text
      x="100"
      y={good ? 30 : 25}
      textAnchor="middle"
      fontSize="9"
      fill="#fff"
      fontWeight="700"
      transform={good ? '' : 'rotate(-5 100 65)'}
    >
      CARTEIRA NACIONAL DE HABILITAÇÃO
    </text>

    {/* Photo placeholder */}
    <rect
      x={good ? 28 : 22}
      y={good ? 44 : 40}
      width="40"
      height="52"
      rx="4"
      fill="#dbeafe"
      stroke={good ? C.azul : '#fca5a5'}
      strokeWidth="1"
      transform={good ? '' : 'rotate(-5 100 65)'}
    />
    <circle
      cx={good ? 48 : 42}
      cy={good ? 60 : 56}
      r="10"
      fill="#bfdbfe"
      transform={good ? '' : 'rotate(-5 100 65)'}
    />
    <path
      d={`M${good ? 32 : 26} ${good ? 96 : 92} Q${good ? 48 : 42} ${
        good ? 82 : 78
      } ${good ? 64 : 58} ${good ? 96 : 92}`}
      fill="#93c5fd"
      transform={good ? '' : 'rotate(-5 100 65)'}
    />

    {/* Text lines */}
    {[0, 1, 2, 3].map(i => (
      <rect
        key={i}
        x={good ? 76 : 70}
        y={good ? 44 + i * 14 : 40 + i * 14}
        width={good ? [70, 55, 65, 45][i] : [60, 45, 55, 35][i]}
        height="6"
        rx="3"
        fill={good ? '#dbeafe' : '#fee2e2'}
        transform={good ? '' : 'rotate(-5 100 65)'}
      />
    ))}

    {/* Category badge */}
    <circle
      cx={good ? 155 : 149}
      cy={good ? 90 : 86}
      r="14"
      fill={good ? C.azul : '#ef4444'}
      transform={good ? '' : 'rotate(-5 100 65)'}
    />
    <text
      x={good ? 155 : 149}
      y={good ? 94 : 90}
      textAnchor="middle"
      fontSize="10"
      fill="#fff"
      fontWeight="900"
      transform={good ? '' : 'rotate(-5 100 65)'}
    >
      AB
    </text>

    {/* Glare / reflection overlay for bad example */}
    {!good && (
      <ellipse
        cx="90"
        cy="60"
        rx="50"
        ry="30"
        fill="white"
        opacity="0.45"
        transform="rotate(-5 100 65)"
      />
    )}

    {/* Badge */}
    <circle cx="178" cy="18" r="13" fill={good ? C.success : C.danger} />
    <text x="178" y="22" textAnchor="middle" fontSize="13" fill="#fff">
      {good ? '✓' : '✗'}
    </text>

    {/* Label */}
    <rect
      x="10"
      y="116"
      width="180"
      height="12"
      rx="4"
      fill={good ? C.success : C.danger}
      opacity="0.12"
    />
    <text
      x="100"
      y="125"
      textAnchor="middle"
      fontSize="9"
      fill={good ? C.success : C.danger}
      fontWeight="700"
    >
      {good
        ? 'FOTO CORRETA — documento plano e legível'
        : 'FOTO INCORRETA — inclinado + reflexo'}
    </text>
  </svg>
);

// ─── Upload area ──────────────────────────────────────────────────────────────
const UploadArea = ({
  file,
  onFile,
  onClear,
  label = 'Toque para fotografar ou escolher da galeria',
}) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setDrag(false);
      const f = e.dataTransfer.files?.[0];
      if (f && f.type.startsWith('image/')) onFile(f);
    },
    [onFile],
  );

  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <div style={{position: 'relative'}}>
      {file && (
        <button
          onClick={onClear}
          style={{
            position: 'absolute',
            top: -10,
            right: -10,
            zIndex: 2,
            background: '#fff',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            width: 28,
            height: 28,
            boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <CancelOutlined style={{fontSize: 18, color: C.danger}} />
        </button>
      )}

      <div
        onDragOver={e => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        style={{
          width: '100%',
          minHeight: file ? 200 : 130,
          borderRadius: 14,
          border: file ? 'none' : `2px dashed ${drag ? C.laranja : C.azul}`,
          background: file
            ? 'transparent'
            : drag
            ? 'rgba(255,102,0,0.05)'
            : '#f4f6fa',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'all 0.2s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{display: 'none'}}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
        {file ? (
          <img
            src={preview}
            alt="preview"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 14,
            }}
            onLoad={() => URL.revokeObjectURL(preview)}
          />
        ) : (
          <div style={{textAlign: 'center', padding: 20}}>
            <div style={{fontSize: 36, marginBottom: 8}}>📄</div>
            <p
              style={{margin: 0, fontSize: 14, fontWeight: 700, color: C.azul}}
            >
              {label}
            </p>
            <p style={{margin: '4px 0 0', fontSize: 12, color: C.muted}}>
              JPG, PNG · máx. 10 MB
            </p>
          </div>
        )}
      </div>

      {file && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 10,
            background: 'rgba(6,68,142,0.05)',
            border: `1px solid ${C.azul}20`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{fontSize: 16}}>🔍</span>
          <p style={{margin: 0, fontSize: 12, color: C.azul, fontWeight: 600}}>
            Confira: documento plano, todos os campos legíveis, sem reflexo.
          </p>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// STEP CNH
// ══════════════════════════════════════════════════════════════════════════════
/**
 * Props
 * ─────
 * authUser       Firebase User
 * getToken       () => Promise<string>
 * sm             boolean
 * driverData     ParceirosDadosmodels | null  — used to get ncnh for upload docId
 * cnhFile / setCnhFile    File | null
 * cnhCat / setCnhCat      string
 * cnhExp / setCnhExp      string  DD/MM/AAAA
 * onNext         () => void
 * onBack         () => void
 */
const StepCnh = ({
  authUser,
  getToken,
  sm,
  driverData,
  cnhFile,
  setCnhFile,
  cnhCat,
  setCnhCat,
  cnhExp,
  setCnhExp,
  onNext,
  onBack,
}) => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [phase, setPhase] = useState('');

  // ── validation ──────────────────────────────────────────────────────────────
  const expValid =
    cnhExp.replace(/\D/g, '').length === 8 ? isFutureDate(cnhExp) : null;
  const daysLeft = expValid ? daysUntil(cnhExp) : null;

  const validate = () => {
    if (!cnhFile) return 'Adicione a foto da sua CNH.';
    if (!cnhCat) return 'Selecione a categoria da sua CNH.';
    if (expValid === false)
      return 'A CNH já está vencida ou a data é inválida.';
    if (expValid === null) return 'Informe a data de validade (DD/MM/AAAA).';
    if (daysLeft !== null && daysLeft < 30)
      return `Sua CNH vence em ${daysLeft} dias. Renove antes de continuar.`;
    return null;
  };

  // ── submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    setLoading(true);

    try {
      const token = await getToken();
      const uid = authUser.uid;

      // docId for S3 = ncnh + 1 (mirrors ViewModel logic)
      // For brand-new drivers ncnh is 0, so docId = "1"
      const ncnh = driverData?.ncnh ?? 0;
      const docId = String(ncnh + 1);

      // 1. Upload CNH photo ────────────────────────────────────────────────────
      setPhase('uploading');
      setUploadPct(10);

      const form = new FormData();
      form.append('file', cnhFile);

      const interval = setInterval(() => {
        setUploadPct(p => (p < 80 ? p + 9 : p));
      }, 300);

      const uploadRes = await fetch(
        `${API_BASE}/s3/upload?userid=${uid}&docid=${docId}&svc=cnh&index=0`,
        {
          method: 'POST',
          headers: {Authorization: `Bearer ${token}`},
          body: form,
        },
      );

      clearInterval(interval);
      setUploadPct(90);

      if (!uploadRes.ok) {
        // Non-fatal: log and continue — server will mark as pending photo
        console.warn('CNH photo upload failed, continuing');
      }

      // 2. Register CNH request ────────────────────────────────────────────────
      setPhase('saving');

      const params = new URLSearchParams({
        id: uid,
        docid: '', // server derives docid from ncnh counter
        exp: cnhExp, // DD/MM/AAAA
        cat: cnhCat,
      });

      const saveRes = await fetch(
        `${API_BASE}/parceiros/cnh/requestupdate?${params}`,
        {
          method: 'PUT',
          headers: {Authorization: `Bearer ${token}`},
        },
      );

      if (!saveRes.ok) {
        throw new Error(`Erro ao registrar CNH (${saveRes.status}).`);
      }

      setUploadPct(100);

      setTimeout(() => onNext(), 400);
    } catch (e) {
      setError(e.message || 'Erro ao enviar. Tente novamente.');
      setLoading(false);
      setPhase('');
      setUploadPct(0);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* ── Photo guide ───────────────────────────────────────────────────── */}
      <p
        style={{
          margin: '0 0 10px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        📄 Foto da CNH (frente)
      </p>
      <p
        style={{
          margin: '0 0 14px',
          fontSize: 13,
          color: C.muted,
          lineHeight: 1.55,
        }}
      >
        Fotografe a frente do documento aberta, com todos os campos visíveis. A
        CNH digital (print da tela do Detran) também é aceita.
      </p>

      {/* Good / bad examples */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div style={{height: sm ? 110 : 130}}>
          <CnhIllustration good />
        </div>
        <div style={{height: sm ? 110 : 130}}>
          <CnhIllustration good={false} />
        </div>
      </div>

      {/* Requirements */}
      <div
        style={{
          background: '#f8f9fb',
          borderRadius: 12,
          padding: '12px 14px',
          border: '1px solid #eef0f4',
          marginBottom: 14,
        }}
      >
        <p
          style={{
            margin: '0 0 8px',
            fontSize: 11,
            fontWeight: 700,
            color: C.muted,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Requisitos da foto
        </p>
        {CNH_REQS.map((r, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 0',
              borderBottom:
                i < CNH_REQS.length - 1 ? '1px solid #f0f2f5' : 'none',
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                background: r.ok ? `${C.success}18` : `${C.danger}18`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 11,
                flexShrink: 0,
                color: r.ok ? C.success : C.danger,
                fontWeight: 700,
              }}
            >
              {r.ok ? '✓' : '✗'}
            </span>
            <span style={{fontSize: 12, color: C.text}}>{r.text}</span>
          </div>
        ))}
      </div>

      {/* Validation timeline banner */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 10,
          marginBottom: 16,
          background: 'rgba(6,68,142,0.05)',
          border: `1px solid ${C.azul}20`,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
        }}
      >
        <span style={{fontSize: 16}}>⏳</span>
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: C.azul,
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          A Irê it leva de 3 a 5 dias úteis para validar o documento. Enquanto
          isso seu cadastro fica em análise.
        </p>
      </div>

      {/* Upload area */}
      <UploadArea
        file={cnhFile}
        onFile={setCnhFile}
        onClear={() => setCnhFile(null)}
        label="Toque para fotografar a frente da CNH"
      />

      {/* Divider */}
      <div style={{height: 1, background: '#f0f2f5', margin: '24px 0'}} />

      {/* ── Category selection ─────────────────────────────────────────────── */}
      <p
        style={{
          margin: '0 0 12px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        🏷️ Categoria da CNH
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: sm ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
          gap: 8,
          marginBottom: 4,
        }}
      >
        {CNH_CATS.map(cat => {
          const sel = cnhCat === cat.id;
          return (
            <button
              key={cat.id}
              onClick={() => setCnhCat(cat.id)}
              disabled={loading}
              style={{
                padding: '10px 4px',
                borderRadius: 12,
                cursor: 'pointer',
                border: `2px solid ${sel ? C.azul : '#e0e5ef'}`,
                background: sel ? `${C.azul}0d` : '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
                transition: 'all 0.15s',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
              }}
            >
              <span style={{fontSize: 16, lineHeight: 1}}>
                {cat.icon.split('').slice(0, 2).join('')}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: sel ? C.azul : C.text,
                }}
              >
                {cat.label}
              </span>
              <span
                style={{
                  fontSize: 9,
                  color: sel ? C.azul : C.muted,
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {cat.desc}
              </span>
            </button>
          );
        })}
      </div>

      {/* Divider */}
      <div style={{height: 1, background: '#f0f2f5', margin: '24px 0'}} />

      {/* ── Expiry date ────────────────────────────────────────────────────── */}
      <p
        style={{
          margin: '0 0 12px',
          fontSize: 15,
          fontWeight: 800,
          color: C.text,
        }}
      >
        📅 Validade da CNH
      </p>

      <InputMask
        mask="99/99/9999"
        value={cnhExp}
        onChange={e => setCnhExp(e.target.value)}
        disabled={loading}
      >
        {() => (
          <TextField
            label="Data de validade *"
            variant="outlined"
            size="small"
            fullWidth
            placeholder="DD/MM/AAAA"
            inputProps={{inputMode: 'numeric'}}
            error={expValid === false}
            helperText={
              expValid === false
                ? 'CNH vencida ou data inválida.'
                : expValid === true && daysLeft !== null && daysLeft < 90
                ? `⚠️ Vence em ${daysLeft} dias — renove em breve.`
                : expValid === true
                ? `✓ Válida por mais ${daysLeft} dias`
                : ''
            }
            FormHelperTextProps={{
              style: {
                color:
                  expValid === true
                    ? daysLeft !== null && daysLeft < 90
                      ? C.warn
                      : C.success
                    : undefined,
                fontWeight: expValid !== null ? 600 : 400,
              },
            }}
          />
        )}
      </InputMask>

      {/* Error */}
      {error && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 14px',
            borderRadius: 10,
            background: `${C.danger}0d`,
            border: `1px solid ${C.danger}30`,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{fontSize: 16}}>⚠️</span>
          <p
            style={{margin: 0, fontSize: 13, color: C.danger, fontWeight: 600}}
          >
            {error}
          </p>
        </div>
      )}

      {/* Upload progress */}
      {loading && (
        <div style={{marginTop: 14}}>
          <p
            style={{
              margin: '0 0 6px',
              fontSize: 12,
              color: C.muted,
              fontWeight: 600,
            }}
          >
            {phase === 'uploading' && `📤 Enviando foto da CNH… ${uploadPct}%`}
            {phase === 'saving' && '⏳ Registrando solicitação…'}
          </p>
          <LinearProgress
            variant={phase === 'uploading' ? 'determinate' : 'indeterminate'}
            value={uploadPct}
            sx={{
              borderRadius: 4,
              height: 6,
              backgroundColor: '#f0f2f5',
              '& .MuiLinearProgress-bar': {backgroundColor: C.laranja},
            }}
          />
        </div>
      )}

      {/* Nav */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 28,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBack />}
          disabled={loading}
          style={{textTransform: 'none', color: C.muted, fontSize: 14}}
        >
          Voltar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          endIcon={loading ? null : <ArrowForward />}
          sx={{
            bgcolor: C.laranja,
            color: '#fff',
            borderRadius: '12px',
            height: 48,
            px: 3,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
            ':hover': {bgcolor: C.laranjaD},
            ':disabled': {bgcolor: '#e0e0e0', color: '#aaa'},
          }}
        >
          {loading ? (
            <CircularProgress size={20} style={{color: '#fff'}} />
          ) : (
            'Enviar CNH'
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepCnh;
